import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { FaDownload, FaPrint } from 'react-icons/fa';
import { BarcodeLabelData, downloadBarcodeLabelPdf } from './generateBarcodeLabelPdf';
import { labelFileName } from './labelHelpers';

export type CodeDisplayMode = 'qrcode' | 'barcode' | 'plu';

interface BarcodeLabelPreviewProps {
  label: BarcodeLabelData;
  includePrice: boolean;
  onAskPrice: (cb: (withPrice: boolean) => void) => void;
  codeMode?: CodeDisplayMode;
}

const BarcodeLabelPreview = ({
  label,
  includePrice,
  onAskPrice,
  codeMode = 'qrcode',
}: BarcodeLabelPreviewProps) => {
  const barcodeCanvasRef = useRef<HTMLCanvasElement>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const [, setQrError] = useState(false);

  useEffect(() => {
    if (codeMode !== 'barcode' || !barcodeCanvasRef.current) return;
    try {
      JsBarcode(barcodeCanvasRef.current, label.value, {
        format: 'CODE128',
        width: 1.5,
        height: 36,
        displayValue: false,
        margin: 0,
      });
    } catch {
      /* non-encodable */
    }
  }, [label.value, codeMode]);

  useEffect(() => {
    if (codeMode !== 'qrcode' || !qrCanvasRef.current) return;
    setQrError(false);
    QRCode.toCanvas(qrCanvasRef.current, label.value, {
      width: 120,
      margin: 1,
      errorCorrectionLevel: 'M',
    }).catch(() => setQrError(true));
  }, [label.value, codeMode]);

  const handleDownload = () => {
    onAskPrice((withPrice) => {
      downloadBarcodeLabelPdf([label], withPrice, labelFileName(codeMode, label.productName, label.value), codeMode);
    });
  };

  const handlePrint = () => {
    onAskPrice((withPrice) => {
      const printWindow = window.open('', '_blank', 'width=400,height=500');
      if (!printWindow) return;

      const priceHtml =
        withPrice && (label.priceRange || label.price)
          ? `<p style="font-size:14px;font-weight:bold;margin:4px 0">${label.priceRange ?? label.price}</p>`
          : '';

      const codeCanvas = codeMode === 'qrcode' ? qrCanvasRef.current : barcodeCanvasRef.current;
      const codeDataUrl = codeCanvas?.toDataURL('image/png') ?? '';

      const imgStyle =
        codeMode === 'qrcode'
          ? 'width:120px;height:120px'
          : 'width:200px;height:50px';

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head><title>Print Label</title></head>
          <body style="text-align:center;font-family:sans-serif;padding:20px">
            ${priceHtml}
            <p style="font-size:10px;color:#555;margin:4px 0">${label.productName}</p>
            <img src="${codeDataUrl}" style="${imgStyle};display:block;margin:8px auto" />
            <p style="font-size:12px;font-family:monospace;margin:4px 0">${label.value}</p>
            <p style="font-size:8px;color:#999;margin:4px 0">www.shopnsmile.org</p>
            <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    });
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.300"
      borderRadius="md"
      p={3}
      bg="white"
      maxW="220px"
      mx="auto"
      textAlign="center">
      <VStack spacing={1}>
        {includePrice && (label.priceRange || label.price) && (
          <Text fontSize="xs" fontWeight="bold" color="gray.800">
            {label.priceRange ?? label.price}
          </Text>
        )}

        <Text fontSize="2xs" color="gray.600" noOfLines={2}>
          {label.productName}
        </Text>

        {codeMode === 'qrcode' && (
          <canvas
            ref={qrCanvasRef}
            style={{ width: 120, height: 120, margin: '4px auto' }}
          />
        )}

        {codeMode === 'barcode' && (
          <canvas
            ref={barcodeCanvasRef}
            style={{ width: '100%', maxHeight: 40 }}
          />
        )}

        <Text fontSize="xs" fontFamily="mono">
          {label.value}
        </Text>

        <Text fontSize="6px" color="gray.400">
          www.shopnsmile.org
        </Text>

        <HStack spacing={1} w="full">
          <Button
            size="xs"
            leftIcon={<FaPrint size={10} />}
            colorScheme="green"
            variant="outline"
            onClick={handlePrint}
            fontSize="2xs"
            flex={1}>
            Print
          </Button>
          <Button
            size="xs"
            leftIcon={<FaDownload size={10} />}
            colorScheme="blue"
            variant="outline"
            onClick={handleDownload}
            fontSize="2xs"
            flex={1}>
            PDF
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default BarcodeLabelPreview;
